import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { message } from 'antd';
import clsx from 'clsx';

import { Product } from 'api/types/product';
import { storeSetLocalProducts, useStoreDetails } from 'api/store';
import { Checkbox, Spin, Typography } from 'ui-kit';
import MyProducts from './MyProducts/MyProducts';
import AdditionFirstProduct from './AdditionFirstProduct';
import DeleteModal from 'components/ProductModal/DeleteModal';
import PreviewProductModal from 'components/PreviewProductModal';
import EditProductModal from '../EditProductModal';
import { ProductLocalStatus } from 'utils/product';
import ConfirmModalMassAddProduct from 'components/ConfirmModalMassAddProduct';

import './Products.scss';
import ConfirmModalMassDeleteProduct from 'components/ConfirmModalMassDeleteProduct';
import { massProductDelete, createProductGroupAndPropagate } from 'api/product_sdk';
import { useLoadingBackdrop } from 'hooks/useLoadingBackdrop';
import { useOwnerDetails } from 'api/owners';
import { useSignedUser } from 'api/auth';

export interface IProducts {
  ggProductsIsLoading: boolean;
  currency: string;
  storeId: number;
  newLocalProducts: Record<string, ProductLocalStatus>;
  productsAreReordering: boolean;
  mutateProducts: () => void;
  className?: string;
  ggProductsData?: Product[];
}

const Products = ({
  ggProductsIsLoading,
  currency,
  storeId,
  newLocalProducts,
  productsAreReordering,
  mutateProducts,
  className,
  ggProductsData,
}: IProducts) => {
  const {
    data: storeData,
    invalidate: invalidateStoreDetails,
    mutate: mutateStoreDetails,
  } = useStoreDetails(storeId);
  const { data: user } = useSignedUser();
  const { data: ownerData } = useOwnerDetails(user?.owner_id);
  const { plan } = ownerData || {};
  const [isLocalProductsDisabled, setLocalProductsDisabled] = useState();
  const { category = 'all' } = useParams<{ category?: string }>();
  const [productToEdit, setProductToEdit] = useState<Product>(null);
  const [previewProduct, setPreviewProduct] = useState<Product>(null);

  const [productToDelete, setProductToDelete] = useState<Partial<Product>>(null);
  const [productToPropagate, setProductToPropagate] = useState<Partial<Product>>(null);
  const [massAddConfirmModalOpen, setMassAddConfirmModalOpen] = useState<boolean>(false);

  const [productForDelete, setProductForDelete] = useState<Partial<Product>>(null);
  const [massDeleteConfirmModalOpen, setMassDeleteConfirmModalOpen] = useState<boolean>(false);

  const [showLoading, hideLoading] = useLoadingBackdrop();

  useEffect(() => {
    setLocalProductsDisabled(storeData.isLocalProductOptedOut);
  }, [storeData]);

  const handlePreviewProduct = ({ id }) => {
    const product = ggProductsData.find((item) => item.id === id);
    setPreviewProduct(product);
  };

  const handleSetEditProduct = ({ id }) => {
    const product = ggProductsData.find((item) => item.id === id);
    setProductToEdit(product);
  };

  const handleCleanEditProduct = () => {
    setProductToEdit(null);
  };

  const handleSetDeleteProduct = (product) => {
    setProductToDelete(product);
  };

  const handleCleanDeleteProduct = () => {
    setProductToDelete(null);
    mutateProducts();
  };

  const handleSetProductToPropagate = ({ id }) => {
    const product = ggProductsData.find((item) => item.id === id);
    setProductToPropagate(product);
    setMassAddConfirmModalOpen(true);
  };
  const handleConfirmMassCreate = useCallback(() => {
    showLoading();

    const productId = productToPropagate.id;
    createProductGroupAndPropagate({ productId })
      .then(() => {
        message.success('Product added successfully to all stores!');
        mutateProducts();
        hideLoading();
      })
      .catch((error) => {
        message.error(error.message || 'Something went wrong! Please try again later');
        hideLoading();
      });
    setMassAddConfirmModalOpen(false);
  }, [productToPropagate, showLoading, hideLoading]);

  const handleCancelMassCreate = useCallback(() => {
    setProductToPropagate(null);
    setMassAddConfirmModalOpen(false);
  }, []);

  //  Mass delete product
  const handleMassDeleteProduct = ({ id }) => {
    const product = ggProductsData.find((item) => item.id === id);
    setProductForDelete(product);
    setMassDeleteConfirmModalOpen(true);
  };

  const handleConfirmMassDeleteProduct = useCallback(() => {
    showLoading();
    setMassDeleteConfirmModalOpen(false);

    const productId = productForDelete.id;
    massProductDelete({ productId })
      .then(() => {
        setProductForDelete(null);
        message.success('Product deleted successfully from all stores!');
        mutateProducts();
        hideLoading();
      })
      .catch((errro) => {
        message.error(errro.message);
        hideLoading();
      });
  }, [productForDelete, showLoading, hideLoading]);

  const handleCancelMassDelete = useCallback(() => {
    setProductForDelete(null);
    setMassDeleteConfirmModalOpen(false);
  }, []);

  const hasLocalProducts = useMemo(
    () => ggProductsData.filter((product) => product.category.includes('LP')).length > 0,
    [ggProductsData],
  );

  const toggleLocalProductsSet = () => {
    const payload = {
      isLocalProductOptedOut: !isLocalProductsDisabled,
    };
    storeSetLocalProducts(storeId, payload)
      .then((res) => {
        if (res.ok) {
          const isLocalProductOptedOut = res.data.isLocalProductOptedOut;
          setLocalProductsDisabled(isLocalProductOptedOut);
          message.success(
            isLocalProductOptedOut ? 'Inactive local vendors' : 'Active local vendors',
          );
          invalidateStoreDetails();
        } else {
          message.error(res.data || 'Error updating vendor activity');
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.log(e);
      });
  };

  return (
    <div className={clsx(className, 'DashboardStoreProducts')}>
      <Spin spinning={ggProductsIsLoading}>
        {ggProductsData.length > 0 ? (
          <MyProducts
            data={ggProductsData}
            newLocalProducts={newLocalProducts}
            currency={currency}
            onEdit={handleSetEditProduct}
            onPreview={handlePreviewProduct}
            onDelete={handleSetDeleteProduct}
            onPropagate={handleSetProductToPropagate}
            onMassDelete={handleMassDeleteProduct}
            storeId={storeId}
            updateStoreProducts={mutateProducts}
            productsAreReordering={productsAreReordering}
          />
        ) : (
          <AdditionFirstProduct />
        )}

        <ConfirmModalMassAddProduct
          isOpen={massAddConfirmModalOpen}
          onConfirm={handleConfirmMassCreate}
          onCancel={handleCancelMassCreate}
        />

        <ConfirmModalMassDeleteProduct
          isOpen={massDeleteConfirmModalOpen}
          onConfirm={handleConfirmMassDeleteProduct}
          onCancel={handleCancelMassDelete}
        />

        <EditProductModal
          product={productToEdit}
          isOpen={Boolean(productToEdit?.id)}
          onCancel={handleCleanEditProduct}
          onSuccess={(values) => {
            setPreviewProduct({ ...productToEdit, ...values });
            handleCleanEditProduct();
            mutateStoreDetails();
          }}
          plan={plan}
        />
        <PreviewProductModal
          product={previewProduct}
          isOpen={Boolean(previewProduct?.id)}
          onClose={() => setPreviewProduct(null)}
          onEdit={handleSetEditProduct}
        />
        {productToDelete && (
          <DeleteModal
            isOpen={Boolean(productToDelete.id)}
            onCancel={handleCleanDeleteProduct}
            onDeleteProduct={handleCleanDeleteProduct}
            product={productToDelete}
          />
        )}
        {!ggProductsIsLoading && category !== 'in-home' && (
          <div className="DashboardStoreLocalProduct_notification">
            <div className="DashboardStoreLocalProduct_notification_checkbox">
              <Checkbox
                id="all"
                onChange={toggleLocalProductsSet}
                checked={isLocalProductsDisabled}
              />
              <Typography variant="body2">Start new local vendors as default inactive</Typography>
            </div>
            {hasLocalProducts ? (
              <Typography variant="body2">
                Want to be notified when new local vendors are added to your area? Email us at
                <a href="mailto: support@thehost.co"> support@thehost.co</a>
              </Typography>
            ) : (
              <Typography variant="body2">
                There are currently no local vendors available in your area. Have one you love or
                want to be notified when new local vendors are added to your area? Email us at
                <a href="mailto: support@thehost.co"> support@thehost.co</a>
              </Typography>
            )}
          </div>
        )}
      </Spin>
    </div>
  );
};

export default Products;
